// Ficha 10 — Bienvenida: Nivel 2 + recarga. Card con head (eyebrow/título/sub), hero verde del
// regalo (promo de bienvenida de la cadena vía /welcome-bonus) y TRES secciones SIEMPRE visibles con
// chip de estado, que arranca en lo que ya trae el expediente: ① perfil transaccional (las cinco de
// PLD más la ocupación) · ② beneficiario 100% (OPCIONAL para la recarga, el silo no lo gatea;
// parentescos 1:1 con BeneficiaryRelationEnum) · ③ anexo versionado (Leer documento = TD-005).
// Activar recarga → upgrade N2 + acreditación (D33, una sola vez; el único gate de esta pantalla es
// el anexo). Salir sin recarga = modal destructivo → Nivel 1 + Pendientes (la VENTA ya está completa).
//
// El comprador que YA TRAÍA cuenta no ve este paso: su cuenta ya tuvo el upgrade a Nivel 2 y su
// expediente ya está completo. Mismo trato que el paso 09 — se brinca, pero se cierra igual.
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { AlertTriangle, ArrowLeft, Check, Gift } from 'lucide-react';
import { Button, ConfirmDialog, Loading } from '@/kit';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import {
    useExitWithoutTopup,
    useProfile,
    useSetBeneficiary,
    useSetProfileQuestions,
    useSignAnnex,
    useWelcomeBonus,
    useWelcomeTopup,
    useWizardSession,
} from '../api/useWizard';
import { wizardErrorMsg } from '../api/wizardError';
import { welcomeTopupErrorMsg } from '../welcomeErrors';
import { offersWelcomeBonus, welcomeCopy } from '../welcomeCopy';
import { Confetti } from '../components/Confetti';
import { accountOrigin, alreadyHadAccount } from '../accountWait';
import { WELCOME_BENEFICIARY_AND_ANNEX } from '../welcomeFeatures';
import { saleRegistrationMissing } from '../saleRegistration';
import {
    ACCOUNT_OPENING_WELCOME_PROFILE,
    ACCOUNT_OPENING_WELCOME_PROFILE_DONE,
    ACCOUNT_OPENING_WELCOME_PROFILE_HINT,
    hasAccountOpeningMark,
} from '../accountOpening';
import { StateIcon } from '../components/KycScreenBits';
import type { Beneficiary, Occupation, ProfileAnswers } from '../types';
import {
    PROFILE_QUESTION_KEYS,
    PROFILE_QUESTION_LABELS,
    ProfileQuestions,
    type ProfileQuestionKey,
} from '../components/ProfileQuestions';
import { WizardCard, WizardCardBody, WizardCardHead } from '../components/WizardCard';
import { WizardBackLink, WizardFootNav } from '../components/WizardFootNav';
import { InlineQueryError, QueryErrorNotice } from '../components/QueryErrorNotice';

// Parentescos del mock — calzan 1:1 con `BeneficiaryRelationEnum` del expediente (la traducción
// al enum vive en el client: SPOUSE/CHILD/PARENT/SIBLING/GRANDPARENT/GRANDCHILD/PARTNER/FRIEND/OTHER).
const RELATIONSHIPS = [
    'Cónyuge',
    'Hijo(a)',
    'Padre / Madre',
    'Hermano(a)',
    'Abuelo(a)',
    'Nieto(a)',
    'Concubino(a)',
    'Amigo(a)',
    'Otro',
];

const BENEFICIARIO_VACIO = {
    firstName: '',
    lastNamePaternal: '',
    lastNameMaternal: '',
    birthDate: '',
    relationship: '',
    sameTitularPhone: true,
    phone: '',
};

/**
 * Las preguntas que el expediente NO tiene contestadas, con la etiqueta que ve el vendedor.
 *
 * Se lee de lo que devuelve el expediente RELEÍDO, no de lo que quedó en pantalla: el guardado puede
 * responder 200 sin que el dato haya llegado a People, y ahí es donde el vendedor necesita el detalle.
 */
function faltantesDelExpediente(preguntas: ProfileAnswers | undefined): string[] {
    if (!preguntas) return [];
    return PROFILE_QUESTION_KEYS.filter((k) => !preguntas[k]).map(
        (k) => PROFILE_QUESTION_LABELS[k],
    );
}

/**
 * Texto del chip de una sección. Mientras el expediente se lee no dice "Pendiente": el primer pintado
 * afirmaba que faltaba un dato que un segundo después aparecía completo.
 */
function chipDe(listo: boolean, textoListo: string, leyendo: boolean): string {
    if (listo) return textoListo;
    return leyendo ? 'Consultando…' : 'Pendiente';
}

/** Cuánto del saldo lleva repartido la lista. El silo exige que cierre en 100 para activar. */
function repartoDe(bens: Beneficiary[]): number {
    return bens.reduce((total, b) => total + (b.percentage ?? 0), 0);
}

export function WelcomeStep({ sessionId }: { sessionId: string }) {
    const { data: session } = useWizardSession(sessionId);
    if (session && hasAccountOpeningMark(session)) {
        return <AccountOpeningWelcome sessionId={sessionId} />;
    }
    return <SaleWelcome sessionId={sessionId} />;
}

/** Apertura: solo el bono. El perfil ya quedó en Verificación y no hay venta que registrar. */
function AccountOpeningWelcome({ sessionId }: { sessionId: string }) {
    const navigate = useNavigate();
    const { data: session } = useWizardSession(sessionId);
    const topup = useWelcomeTopup(sessionId);
    const exitNoTopup = useExitWithoutTopup(sessionId);
    const bonus = useWelcomeBonus(sessionId, !!session);
    const copy = welcomeCopy(bonus.data);
    const withBonus = offersWelcomeBonus(bonus.data);
    const [exitOpen, setExitOpen] = useState(false);
    if (!session) return <Loading size="lg" />;

    const toClosing = (): Promise<void> =>
        navigate({ to: '/wizard/s/$sessionId/$step', params: { sessionId, step: 'summary' } });

    const receive = async (): Promise<void> => {
        try {
            await topup.mutateAsync();
            await toClosing();
        } catch (e) {
            toast.error(welcomeTopupErrorMsg(e));
        }
    };

    const exit = async (): Promise<void> => {
        try {
            await exitNoTopup.mutateAsync();
            setExitOpen(false);
            await toClosing();
        } catch (e) {
            setExitOpen(false);
            toast.error(wizardErrorMsg(e, 'No se pudo registrar la salida — intenta de nuevo.'));
        }
    };

    return (
        <WizardCard>
            <WizardCardHead eyebrow="Bienvenida" title={copy.title} />
            <WizardCardBody>
                {withBonus && <WelcomeGiftHero bonus={bonus} celebrate={topup.isSuccess} />}
                <Section
                    n={1}
                    title={ACCOUNT_OPENING_WELCOME_PROFILE}
                    done
                    chip={ACCOUNT_OPENING_WELCOME_PROFILE_DONE}
                >
                    <p className="text-[11.5px] leading-relaxed text-muted-foreground">
                        {ACCOUNT_OPENING_WELCOME_PROFILE_HINT}
                    </p>
                </Section>
                {topup.isError && (
                    <p className="mx-auto mt-3.5 max-w-md rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-[11.5px] leading-relaxed text-destructive">
                        {welcomeTopupErrorMsg(topup.error)}
                    </p>
                )}
            </WizardCardBody>
            <WizardFootNav
                back={<WizardBackLink sessionId={sessionId} step="referrals" />}
                showExit={withBonus}
                exit={
                    <Button
                        variant="ghost"
                        onClick={() => {
                            setExitOpen(true);
                        }}
                    >
                        {copy.exitLabel}
                    </Button>
                }
            >
                <Button
                    isLoading={topup.isPending}
                    rightIcon={<Check className="size-3.5" aria-hidden />}
                    onClick={() => void receive()}
                >
                    {copy.continueLabel}
                </Button>
            </WizardFootNav>
            {withBonus && (
                <ConfirmDialog
                    open={exitOpen}
                    onOpenChange={setExitOpen}
                    title={copy.exitTitle}
                    description={copy.exitDescription}
                    confirmLabel={copy.exitLabel}
                    destructive
                    loading={exitNoTopup.isPending}
                    onConfirm={() => void exit()}
                />
            )}
        </WizardCard>
    );
}

function SaleWelcome({ sessionId }: { sessionId: string }) {
    const navigate = useNavigate();
    const { data: session } = useWizardSession(sessionId);
    // A quien ya traía cuenta este paso no le aplica. Es `alreadyHadAccount`, NO `accountAlreadyOpen`
    // — ver su nota en accountWait.
    const skipsWelcome = !!session && alreadyHadAccount(accountOrigin(session));
    const {
        data: perfil,
        isPending: leyendoExpediente,
        error: profileError,
        isFetching: profileFetching,
        refetch: refetchProfile,
    } = useProfile(sessionId);
    const setQuestions = useSetProfileQuestions(sessionId);
    const setBeneficiary = useSetBeneficiary(sessionId);
    const signAnnex = useSignAnnex(sessionId);
    const topup = useWelcomeTopup(sessionId);
    const exitNoTopup = useExitWithoutTopup(sessionId);
    // Bono vigente de la cadena (su promo de bienvenida): si aplica y su monto, nunca quemado.
    // Espera a la sesión: sin ella `skipsWelcome` todavía es false y el paso que se brinca lo pediría.
    const bonus = useWelcomeBonus(sessionId, !!session && !skipsWelcome);
    const copy = welcomeCopy(bonus.data);

    const [ben, setBen] = useState(BENEFICIARIO_VACIO);
    /**
     * El reparto que se está armando. Se acumula en memoria y viaja ENTERO al guardar: el expediente
     * de Fiado acumula por su cuenta, así que mandarlos de a uno dejaría el saldo repartido a medias
     * si el segundo falla.
     */
    const [bens, setBens] = useState<Beneficiary[]>([]);
    /** `0` = "todo lo que quede", que es el caso de uno solo y evita teclear "100". */
    const [porcentajeNuevo, setPorcentajeNuevo] = useState(0);
    /** Lo que ya lleva asignado la lista pendiente. Manda el gate del botón de guardar. */
    const repartido = repartoDe(bens);
    /** Reabre la captura con el reparto ya guardado, para corregir un porcentaje sin rehacer la venta. */
    const [editandoReparto, setEditandoReparto] = useState(false);
    const [annexOk, setAnnexOk] = useState(false);
    const [exitOpen, setExitOpen] = useState(false);
    /** Lo que el vendedor edita encima de lo que ya trae el expediente. */
    const [respuestas, setRespuestas] = useState<Record<string, string>>({});
    /**
     * Las que el expediente NO registró en el último guardado. `null` = todavía no se guardó, o el
     * guardado quedó confirmado. Un toast se va en segundos y el vendedor tiene al cliente enfrente:
     * el detalle se queda en pantalla hasta el siguiente intento.
     */
    const [sinRegistrar, setSinRegistrar] = useState<string[] | null>(null);
    /** El guardado no termina en el 200: sigue la relectura que lo confirma. El botón espera las dos. */
    const [confirmandoPerfil, setConfirmandoPerfil] = useState(false);

    // 🔴 Brincar el paso NO es dejarlo sin cerrar: `welcomeTopup` es lo único que mueve el cursor a
    // S10 (el cierre de la venta del 09 no lo toca), así que sin llamarlo reanudar vuelve al cobro.
    // El silo corta antes de los gates de recarga: ni reserva, ni acredita, ni pausa la sesión.
    const closeStep = (): void => {
        void topup.mutateAsync().catch(() => undefined); // el error queda en topup.error
    };
    const stepClosed = topup.isSuccess;
    // El cierre automático también se corta con la venta sin registrar: el efecto corre aunque la
    // pantalla devuelva temprano, así que sin esto el gate se vería y el silo cerraría igual.
    const saleUnregistered = !!session && saleRegistrationMissing(session);
    useEffect(() => {
        if (!skipsWelcome || saleUnregistered) return;
        if (topup.isPending || topup.isSuccess || topup.isError) return;
        closeStep();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skipsWelcome, saleUnregistered, topup.isPending, topup.isSuccess, topup.isError]);

    // Solo se avanza con el paso ya cerrado: si falla, el vendedor se queda con el motivo y el
    // Reintentar a la vista en vez de irse al resumen con la venta a medias.
    useEffect(() => {
        if (!skipsWelcome || saleUnregistered || !stepClosed) return;
        void navigate({
            to: '/wizard/s/$sessionId/$step',
            params: { sessionId, step: 'summary' },
        });
    }, [skipsWelcome, saleUnregistered, stepClosed, navigate, sessionId]);

    if (!session) return <Loading size="lg" />;

    // ── La venta no quedó registrada: no hay sobre qué dar bienvenida ─────────
    // 🔴 Va antes que todo lo demás porque este paso ACREDITA DINERO, y aquí se llega sin pasar por
    // el gate del paso 09 (deep-link, el reanudador y las dos derivaciones). La derivación externa
    // queda fuera: nunca cierra venta, así que su `saleId` ausente no es una falla.
    if (saleUnregistered) {
        return (
            <WizardCard>
                <WizardCardHead
                    eyebrow="Bienvenida"
                    title="La venta no quedó registrada"
                    sub="No se puede dar la bienvenida hasta que la venta tenga su referencia."
                />
                <WizardCardBody>
                    <div className="text-center">
                        <StateIcon tone="warning">
                            <AlertTriangle className="size-7" aria-hidden />
                        </StateIcon>
                        <p className="mb-1 text-[15px] font-semibold">Falta registrar la venta</p>
                        <p className="mx-auto max-w-md text-[12.5px] leading-relaxed text-muted-foreground">
                            Regresa a Entrega.
                        </p>
                    </div>
                </WizardCardBody>
                <WizardFootNav
                    back={
                        <Button
                            leftIcon={<ArrowLeft className="size-3.5" aria-hidden />}
                            onClick={() =>
                                void navigate({
                                    to: '/wizard/s/$sessionId/$step',
                                    params: { sessionId, step: 'delivery' },
                                })
                            }
                        >
                            Volver a la entrega
                        </Button>
                    }
                />
            </WizardCard>
        );
    }

    // ── El cliente ya traía cuenta: no hay bienvenida que darle ───────────────
    // Pantalla de paso, no de trámite: el efecto de arriba avanza al resumen en cuanto el paso queda
    // cerrado en el silo. Si el cierre falla se queda aquí, con el motivo y el Reintentar.
    if (skipsWelcome) {
        return (
            <WizardCard>
                <WizardCardHead
                    eyebrow="Bienvenida"
                    title="Bienvenida"
                    sub="Este cliente no necesita nada de este paso."
                />
                <WizardCardBody>
                    <div className="text-center">
                        <div className="mx-auto mb-3 flex size-14 items-center justify-center rounded-full bg-success-soft text-success">
                            <Check className="size-7" strokeWidth={3} aria-hidden />
                        </div>
                        <p className="mb-1 text-[15px] font-semibold">
                            El cliente ya tenía su cuenta PagoConFiado
                        </p>
                        <p className="mx-auto max-w-md text-[12.5px] leading-relaxed text-muted-foreground">
                            Cerrando la venta…
                        </p>
                    </div>
                    {/* Cómo va el cierre. Vive en el cuerpo: el pie es solo navegación. */}
                    <div className="mt-4 text-center">
                        <StepCloseStatus
                            error={
                                topup.isError
                                    ? wizardErrorMsg(topup.error, 'No se pudo cerrar la venta.')
                                    : null
                            }
                            retrying={topup.isPending}
                            onRetry={closeStep}
                        />
                    </div>
                </WizardCardBody>
                <WizardFootNav />
            </WizardCard>
        );
    }

    const w = session.welcome;
    /** Los que Fiado ya tiene registrados — fuente real, no el flag de la sesión. */
    const beneficiarios = perfil?.beneficiarios.items ?? [];
    const benDone = beneficiarios.length > 0 || w.benefCaptured || !!w.beneficiary;
    /**
     * Lo que el cliente ya contestó en el paso 02, espejado en la sesión de esta venta. Cubre el
     * hueco entre capturarlo y verlo en el expediente: sin esto el vendedor le repregunta lo mismo
     * dos pantallas después.
     */
    const fromStep02: Partial<Record<ProfileQuestionKey, string>> = {
        occupation: session.occupation?.code,
        monthlyIncome: session.monthlyIncome,
    };
    /**
     * De más fuerte a más débil: lo que el vendedor edita ahora, lo que declara el expediente, y lo
     * capturado en el paso 02 de esta misma venta.
     */
    const valorPregunta = (k: ProfileQuestionKey): string =>
        k in respuestas ? respuestas[k] : (perfil?.preguntas[k] ?? fromStep02[k] ?? '');
    /** Hay con qué guardar: las seis tienen valor en pantalla, vengan de donde vengan. */
    const preguntasListas = PROFILE_QUESTION_KEYS.every((k) => valorPregunta(k) !== '');
    /**
     * Lo que el EXPEDIENTE declara — el único que puede marcar la sección completa. Una pregunta
     * prellenada con el espejo del paso 02 se ve contestada pero todavía no llegó a People: dar la
     * sección por cerrada haría que el vendedor la salte y el dato nunca se guarde.
     */
    const preguntasEnExpediente = perfil?.preguntasCompletas ?? false;

    const guardarPreguntas = async (): Promise<void> => {
        // Manda las SEIS y reemplaza el expediente, así que no se escribe sin haberlo leído. Paliativo
        // del front: cualquier otro cliente puede pisar PLD parcial — el candado real es del silo.
        if (profileError) {
            toast.error('No se puede guardar el perfil sin haber leído el expediente del cliente.');
            return;
        }
        // Sin contestar viaja `undefined`, no cadena vacía: el silo valida la ocupación contra el
        // enum y un '' rebota con 400 en vez de tratarse como "no la tengo".
        const ocupacion = valorPregunta('occupation');
        setSinRegistrar(null);
        setConfirmandoPerfil(true);
        try {
            await setQuestions.mutateAsync({
                occupation: ocupacion === '' ? undefined : (ocupacion as Occupation),
                monthlyIncome: valorPregunta('monthlyIncome'),
                monthlyExtraSupport: valorPregunta('monthlyExtraSupport'),
                usageMonthly: valorPregunta('usageMonthly'),
                maximumTx: valorPregunta('maximumTx'),
                beneficialOwner: valorPregunta('beneficialOwner'),
            });
        } catch (e) {
            // El wizard rechaza "por cuenta de un tercero" con un motivo legible: con ese valor la
            // cuenta no puede abrir, y es mejor decirlo aquí que dejarlo morir en otro lambda.
            toast.error(wizardErrorMsg(e, 'No se pudo guardar el perfil — intenta de nuevo.'));
            setConfirmandoPerfil(false);
            return;
        }

        // 🔴 El 200 del guardado NO prueba que el dato haya llegado al expediente: identity rechaza
        // el update cuando la persona ya tiene ocupación y el wizard lo trata como éxito, así que la
        // pantalla cantaba "Perfil guardado" con las cuatro de PLD sin escribir. Manda la relectura.
        const relectura = await refetchProfile();
        setConfirmandoPerfil(false);
        const releido = relectura.data;
        // Con la relectura caída no se sabe si quedó o no. `data` conserva lo leído ANTES de guardar,
        // así que darlo por bueno sería declarar pendiente algo que quizá sí se escribió.
        if (relectura.error || !releido) {
            toast.warning('Se guardó el perfil, pero no pudimos confirmarlo en su expediente.');
            return;
        }
        if (releido.preguntasCompletas) {
            toast.success('Perfil guardado y confirmado en su expediente.');
            return;
        }
        const faltantes = faltantesDelExpediente(releido.preguntas);
        setSinRegistrar(faltantes);
        toast.error(
            faltantes.length > 0
                ? `Su expediente no registró: ${faltantes.join(', ')}. Intenta guardar de nuevo.`
                : 'Su expediente no quedó completo. Intenta guardar de nuevo.',
        );
    };

    /** Agrega el del formulario a la lista pendiente. Todavía no viaja a ningún lado. */
    const agregarBeneficiario = (): void => {
        if (!ben.firstName || !ben.relationship) return;
        const usado = repartoDe(bens);
        // `0` significa "lo que quede": con un solo beneficiario el vendedor no teclea nada y se
        // lleva el 100, que es el caso de tienda.
        const pct = porcentajeNuevo > 0 ? porcentajeNuevo : 100 - usado;
        if (pct <= 0 || usado + pct > 100) {
            toast.error(`Solo queda ${String(100 - usado)}% por repartir.`);
            return;
        }
        setBens([
            ...bens,
            // "Mismo teléfono que el titular" → viaja el de la SESIÓN (contrato del silo:
            // phoneNumber siempre resuelto; el checkbox es un atajo de captura, no un dato).
            {
                ...ben,
                phone: ben.sameTitularPhone ? session.phone : ben.phone,
                percentage: pct,
            },
        ]);
        setBen(BENEFICIARIO_VACIO);
        setPorcentajeNuevo(0);
    };

    /**
     * Manda el reparto COMPLETO en una llamada.
     *
     * No se guarda de a uno: el expediente de Fiado ACUMULA, así que si el segundo falla el cliente
     * queda con el saldo repartido a medias y sin forma de saber cuánto entró.
     */
    const saveBeneficiary = async (): Promise<void> => {
        if (repartoDe(bens) !== 100) return;
        try {
            await setBeneficiary.mutateAsync(bens);
            setBens([]);
            setEditandoReparto(false);
            toast.success(bens.length === 1 ? 'Beneficiario guardado' : 'Beneficiarios guardados');
        } catch (e) {
            toast.error(
                wizardErrorMsg(e, 'No se pudo guardar el beneficiario — intenta de nuevo.'),
            );
        }
    };

    const activate = async (): Promise<void> => {
        try {
            if (annexOk && !w.annexSigned) await signAnnex.mutateAsync();
            await topup.mutateAsync();
            await navigate({
                to: '/wizard/s/$sessionId/$step',
                params: { sessionId, step: 'summary' },
            });
        } catch (e) {
            // El mensaje sale de `welcomeTopupErrorMsg`, que traduce los dos códigos donde el `msg`
            // del silo no dice qué hacer. Se pinta además abajo: un toast se va y esto mueve dinero.
            toast.error(welcomeTopupErrorMsg(e));
        }
    };

    const exit = async (): Promise<void> => {
        try {
            await exitNoTopup.mutateAsync();
            setExitOpen(false);
            await navigate({
                to: '/wizard/s/$sessionId/$step',
                params: { sessionId, step: 'summary' },
            });
        } catch (e) {
            setExitOpen(false);
            toast.error(wizardErrorMsg(e, 'No se pudo registrar la salida — intenta de nuevo.'));
        }
    };

    // Ninguna guardada en vuelo. Sin esto el vendedor alcanza a activar la recarga mientras el
    // silo todavía no confirma el beneficiario, y termina la venta sobre datos que quizá no entraron.
    const guardando =
        setQuestions.isPending ||
        confirmandoPerfil ||
        setBeneficiary.isPending ||
        signAnnex.isPending ||
        profileFetching;

    // La recarga exige el perfil completo en el expediente y sin error; el anexo solo si está prendido.
    const canActivate =
        preguntasEnExpediente &&
        !profileError &&
        (!WELCOME_BENEFICIARY_AND_ANNEX || annexOk || w.annexSigned) &&
        !guardando;

    return (
        <WizardCard>
            <WizardCardHead eyebrow="Bienvenida" title={copy.title} />

            <WizardCardBody>
                {offersWelcomeBonus(bonus.data) && (
                    <WelcomeGiftHero bonus={bonus} celebrate={stepClosed} />
                )}

                {/* ① Perfil transaccional — las cinco de PLD más la ocupación. SIEMPRE visible,
                    igual que el resto: el vendedor debe ver qué declara el expediente. */}
                <Section
                    n={1}
                    title="Revisa su perfil"
                    done={preguntasEnExpediente}
                    chip={
                        profileError
                            ? 'No se pudo leer'
                            : chipDe(preguntasEnExpediente, 'Completo', leyendoExpediente)
                    }
                >
                    {/* 🔴 Lo que se guarda aquí REEMPLAZA el expediente de PLD. Sin poder leerlo,
                        las cuatro preguntas que no vienen del paso 02 salen en blanco: se siguen
                        mostrando —lo del paso 02 es dato real— pero no se dejan guardar encima. */}
                    <QueryErrorNotice
                        className="mx-0 mb-3 max-w-none"
                        title="No se pudo leer el expediente del cliente"
                        error={profileError}
                        fallback="No se pudo consultar lo que el cliente ya declaró."
                        hint="No se puede guardar hasta poder leerlo: lo que se guarde aquí reemplaza su expediente."
                        onRetry={() => void refetchProfile()}
                        retrying={profileFetching}
                    />
                    <ProfileQuestions
                        value={valorPregunta}
                        onChange={(k, v) => {
                            setRespuestas((r) => ({ ...r, [k]: v }));
                        }}
                        canSave={preguntasListas && !profileError}
                        saving={setQuestions.isPending || confirmandoPerfil}
                        onSave={() => void guardarPreguntas()}
                    />
                    {/* El guardado respondió correcto y el expediente no lo recibió. Se nombran las
                        que faltan: sin eso el vendedor no sabe si reintentar o si el problema es otro. */}
                    {sinRegistrar !== null && (
                        <p
                            role="status"
                            className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-[11.5px] leading-relaxed"
                        >
                            <span className="font-semibold">
                                No quedaron registradas en el expediente
                            </span>
                            {sinRegistrar.length > 0 ? `: ${sinRegistrar.join(', ')}.` : '.'}{' '}
                            Intenta guardar de nuevo. Si vuelve a pasar, repórtalo: el guardado
                            responde correcto pero el expediente no lo recibe.
                        </p>
                    )}
                </Section>

                {WELCOME_BENEFICIARY_AND_ANNEX && (
                    <>
                        {/* ② Beneficiario — SIEMPRE visible, con chip de estado. OPCIONAL para la recarga:
                    el silo no lo gatea (doc F2 explícita). */}
                        <Section
                            n={2}
                            title="Registrar un beneficiario"
                            done={benDone}
                            chip={
                                // Con la sesión diciendo que ya los capturó, «Completo» sigue siendo cierto
                                // aunque el expediente no se lea. Sin eso no sabemos qué tiene el cliente.
                                profileError && !benDone
                                    ? 'No se pudo leer'
                                    : chipDe(benDone, 'Completo', leyendoExpediente)
                            }
                        >
                            {benDone && !editandoReparto ? (
                                /* Se listan los que Fiado tiene. Si el expediente no los devolvió (el GET
                           falló), se cae a lo que sepa la sesión — pero sin inventar una lista. */
                                beneficiarios.length > 0 ? (
                                    <ul className="space-y-1 text-[12.5px]">
                                        {beneficiarios.map((b) => (
                                            <li key={b.nombreCompleto}>
                                                {b.nombreCompleto}
                                                {b.relacion ? ` · ${b.relacion}` : ''} ·{' '}
                                                {b.porcentaje}%
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-[12.5px]">
                                        {w.beneficiary
                                            ? `${w.beneficiary.firstName} ${w.beneficiary.lastNamePaternal} · ${w.beneficiary.relationship} · 100%`
                                            : 'Beneficiario registrado · 100%'}
                                    </p>
                                )
                            ) : null}

                            {/* Corregir un porcentaje mal capturado no puede exigir rehacer la venta. Se
                        puede porque el expediente REEMPLAZA el conjunto: reenviar es corregir.

                        🔴 El botón aparece aunque la lista NO se haya podido leer. Antes se pedía
                        `beneficiarios.length > 0` y, con el GET del expediente caído, el vendedor se
                        quedaba con "Beneficiario registrado" y CERO salidas. Que no sepamos qué hay
                        no es razón para no dejar arreglarlo — el reenvío pisa lo que haya. */}
                            {benDone && !editandoReparto && (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    className="mt-2 w-full sm:w-auto"
                                    onClick={() => {
                                        // Se rehidrata con lo guardado para que el vendedor toque solo lo
                                        // que está mal, en vez de recapturar a todos. Si el expediente no se
                                        // pudo leer se arranca en blanco — y el aviso de abajo lo dice.
                                        setBens(
                                            beneficiarios.map((b) => ({
                                                firstName: b.nombres ?? b.nombreCompleto,
                                                lastNamePaternal: b.apellidoPaterno ?? '',
                                                lastNameMaternal: b.apellidoMaterno ?? '',
                                                birthDate: b.fechaNacimiento ?? '',
                                                relationship: b.relacion ?? '',
                                                sameTitularPhone: !b.telefono,
                                                phone: b.telefono,
                                                percentage: b.porcentaje,
                                            })),
                                        );
                                        setEditandoReparto(true);
                                    }}
                                >
                                    {beneficiarios.length > 0
                                        ? 'Corregir el reparto'
                                        : 'Volver a capturar el reparto'}
                                </Button>
                            )}

                            {(!benDone || editandoReparto) && (
                                <>
                                    <p className="mb-3 text-[11.5px] leading-relaxed text-muted-foreground">
                                        Recibe el saldo si el titular fallece. Obligatorio para
                                        enviar dinero.
                                    </p>

                                    {/* Reemplazar a ciegas es legítimo, pero hay que decirlo: si el
                                expediente no se pudo leer, lo que se guarde PISA lo que hubiera. */}
                                    {editandoReparto && beneficiarios.length === 0 && (
                                        <p className="mb-3 rounded-md border border-warning-soft-border bg-warning-soft px-3 py-2 text-[11.5px] leading-relaxed text-warning">
                                            No pudimos leer los beneficiarios que ya tiene el
                                            cliente. Captura el reparto completo: lo que guardes
                                            reemplaza lo que hubiera en su expediente.
                                        </p>
                                    )}

                                    {bens.length > 0 && (
                                        <ul className="mb-3 space-y-1">
                                            {bens.map((b, i) => (
                                                <li
                                                    key={`${b.firstName}-${b.lastNamePaternal}-${String(i)}`}
                                                    className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2"
                                                >
                                                    <span className="min-w-0 flex-1 break-words text-[12.5px]">
                                                        {[
                                                            b.firstName,
                                                            b.lastNamePaternal,
                                                            b.lastNameMaternal,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(' ')}
                                                        <span className="text-muted-foreground">
                                                            {' · '}
                                                            {b.relationship}
                                                        </span>
                                                    </span>
                                                    <span className="shrink-0 text-[12px] font-semibold tabular-nums">
                                                        {b.percentage ?? 0}%
                                                    </span>
                                                    {/* Editar devuelve al formulario lo ya capturado y lo
                                                saca de la lista, para que el porcentaje libre se
                                                recalcule. Sin esto, arreglar un dígito obligaba a
                                                quitar y teclear todo de nuevo. */}
                                                    <button
                                                        type="button"
                                                        aria-label={`Editar a ${b.firstName}`}
                                                        className="shrink-0 text-[11.5px] text-muted-foreground underline decoration-dotted hover:text-foreground"
                                                        onClick={() => {
                                                            setBen({
                                                                firstName: b.firstName,
                                                                lastNamePaternal:
                                                                    b.lastNamePaternal,
                                                                lastNameMaternal:
                                                                    b.lastNameMaternal,
                                                                birthDate: b.birthDate,
                                                                relationship: b.relationship,
                                                                sameTitularPhone:
                                                                    b.sameTitularPhone,
                                                                phone: b.phone ?? '',
                                                            });
                                                            setPorcentajeNuevo(b.percentage ?? 0);
                                                            setBens(bens.filter((_, j) => j !== i));
                                                        }}
                                                    >
                                                        Editar
                                                    </button>
                                                    <button
                                                        type="button"
                                                        aria-label={`Quitar a ${b.firstName}`}
                                                        className="shrink-0 text-[11.5px] text-muted-foreground underline decoration-dotted hover:text-foreground"
                                                        onClick={() => {
                                                            setBens(bens.filter((_, j) => j !== i));
                                                        }}
                                                    >
                                                        Quitar
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}

                                    {/* El acumulado a la vista: sin esto el vendedor no sabe por qué el botón
                                de guardar no se enciende. */}
                                    {bens.length > 0 && (
                                        <p
                                            className={cn(
                                                'mb-3 text-[11.5px]',
                                                repartido === 100
                                                    ? 'font-medium text-success'
                                                    : 'text-warning',
                                            )}
                                        >
                                            {repartido === 100
                                                ? 'Reparto completo: 100%.'
                                                : `Repartido ${String(repartido)}% — faltan ${String(100 - repartido)}% por asignar.`}
                                        </p>
                                    )}
                                    <div className="mb-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                                        <input
                                            aria-label="Nombre(s) del beneficiario"
                                            placeholder="Nombre(s)"
                                            value={ben.firstName}
                                            onChange={(e) => {
                                                setBen({ ...ben, firstName: e.target.value });
                                            }}
                                            className={inputCls}
                                        />
                                        <input
                                            aria-label="Apellido paterno del beneficiario"
                                            placeholder="Apellido paterno"
                                            value={ben.lastNamePaternal}
                                            onChange={(e) => {
                                                setBen({
                                                    ...ben,
                                                    lastNamePaternal: e.target.value,
                                                });
                                            }}
                                            className={inputCls}
                                        />
                                        <input
                                            aria-label="Apellido materno del beneficiario"
                                            placeholder="Apellido materno"
                                            value={ben.lastNameMaternal}
                                            onChange={(e) => {
                                                setBen({
                                                    ...ben,
                                                    lastNameMaternal: e.target.value,
                                                });
                                            }}
                                            className={inputCls}
                                        />
                                    </div>
                                    <div className="mb-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                        <input
                                            aria-label="Fecha de nacimiento del beneficiario"
                                            type="date"
                                            value={ben.birthDate}
                                            onChange={(e) => {
                                                setBen({ ...ben, birthDate: e.target.value });
                                            }}
                                            className={inputCls}
                                        />
                                        <select
                                            aria-label="Parentesco del beneficiario"
                                            value={ben.relationship}
                                            onChange={(e) => {
                                                setBen({ ...ben, relationship: e.target.value });
                                            }}
                                            className={inputCls}
                                        >
                                            <option value="">Parentesco…</option>
                                            {RELATIONSHIPS.map((r) => (
                                                <option key={r} value={r}>
                                                    {r}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="mb-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                                        <label className="col-span-2 flex h-8 cursor-pointer items-center gap-2 rounded-md border border-border bg-muted/40 px-2 text-[11px] leading-tight">
                                            <input
                                                type="checkbox"
                                                checked={ben.sameTitularPhone}
                                                onChange={(e) => {
                                                    setBen({
                                                        ...ben,
                                                        sameTitularPhone: e.target.checked,
                                                    });
                                                }}
                                                className="size-3.5 accent-success"
                                            />
                                            Mismo teléfono que el titular
                                        </label>
                                        <label className="flex h-8 items-center justify-between gap-1 rounded-md border border-border bg-background px-2">
                                            <span className="shrink-0 text-[11.5px] text-muted-foreground">
                                                Porcentaje
                                            </span>
                                            <input
                                                aria-label="Porcentaje del beneficiario"
                                                type="text"
                                                inputMode="numeric"
                                                // Vacío = "lo que quede". Con uno solo eso es 100 y el
                                                // vendedor no teclea nada.
                                                placeholder={String(Math.max(0, 100 - repartido))}
                                                value={
                                                    porcentajeNuevo > 0
                                                        ? String(porcentajeNuevo)
                                                        : ''
                                                }
                                                onChange={(e) => {
                                                    const n = Number(
                                                        e.target.value
                                                            .replace(/\D/g, '')
                                                            .slice(0, 3),
                                                    );
                                                    setPorcentajeNuevo(Number.isFinite(n) ? n : 0);
                                                }}
                                                className="w-10 bg-transparent text-right text-[12px] font-semibold tabular-nums outline-none"
                                            />
                                            <span className="shrink-0 text-[12px] font-semibold">
                                                %
                                            </span>
                                        </label>
                                    </div>
                                    {!ben.sameTitularPhone && (
                                        <input
                                            aria-label="Teléfono del beneficiario"
                                            type="tel"
                                            inputMode="numeric"
                                            maxLength={10}
                                            placeholder="Teléfono del beneficiario (10 dígitos)"
                                            value={ben.phone}
                                            onChange={(e) => {
                                                setBen({
                                                    ...ben,
                                                    phone: e.target.value
                                                        .replace(/\D/g, '')
                                                        .slice(0, 10),
                                                });
                                            }}
                                            className={cn(inputCls, 'mb-2 w-full tabular-nums')}
                                        />
                                    )}
                                    <p className="text-[11px] leading-[1.35] text-muted-foreground">
                                        Por defecto el beneficiario usa el mismo teléfono del
                                        titular. Desmarca la casilla si usa otro.
                                    </p>
                                    <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="w-full sm:w-auto"
                                            onClick={agregarBeneficiario}
                                            disabled={
                                                !ben.firstName ||
                                                !ben.relationship ||
                                                repartido >= 100 ||
                                                setBeneficiary.isPending
                                            }
                                        >
                                            {bens.length === 0
                                                ? 'Agregar beneficiario'
                                                : 'Agregar otro beneficiario'}
                                        </Button>
                                        {/* Solo se guarda con el reparto cerrado: el silo rechaza cualquier
                                    otra suma, y anunciarlo aquí evita el viaje al 400. */}
                                        <Button
                                            size="sm"
                                            className="w-full sm:w-auto"
                                            isLoading={setBeneficiary.isPending}
                                            onClick={() => void saveBeneficiary()}
                                            disabled={repartido !== 100}
                                        >
                                            {setBeneficiary.isPending
                                                ? 'Guardando…'
                                                : bens.length > 1
                                                  ? 'Guardar beneficiarios'
                                                  : 'Guardar beneficiario'}
                                        </Button>
                                        {editandoReparto && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="w-full sm:w-auto"
                                                disabled={setBeneficiary.isPending}
                                                onClick={() => {
                                                    setEditandoReparto(false);
                                                    setBens([]);
                                                    setBen(BENEFICIARIO_VACIO);
                                                    setPorcentajeNuevo(0);
                                                }}
                                            >
                                                Cancelar
                                            </Button>
                                        )}
                                    </div>
                                </>
                            )}
                        </Section>

                        {/* ③ Anexo — el único gate de "Activar recarga". Marcar la casilla FIRMA de una
                    vez: una casilla palomeada no puede convivir con el chip en "Pendiente". */}
                        <Section
                            n={3}
                            title="Aceptar anexo del contrato"
                            done={annexOk || w.annexSigned}
                            chip={annexOk || w.annexSigned ? 'Aceptado' : 'Pendiente'}
                        >
                            <label className="grid cursor-pointer grid-cols-[auto_1fr] items-start gap-x-3 gap-y-0.5 rounded-md border border-border bg-card p-3 hover:bg-accent/40">
                                <input
                                    type="checkbox"
                                    checked={annexOk || w.annexSigned}
                                    disabled={w.annexSigned || signAnnex.isPending}
                                    onChange={(e) => {
                                        const marcado = e.target.checked;
                                        setAnnexOk(marcado);
                                        if (!marcado || w.annexSigned) return;
                                        // Optimista: la palomita se ve al instante y se revierte si la firma
                                        // no aterriza. Dejarla marcada sobre una firma que falló sería la
                                        // misma mentira que el chip en "Pendiente", al revés.
                                        void signAnnex.mutateAsync().catch((err: unknown) => {
                                            setAnnexOk(false);
                                            toast.error(
                                                wizardErrorMsg(
                                                    err,
                                                    'No se pudo aceptar el anexo — intenta de nuevo.',
                                                ),
                                            );
                                        });
                                    }}
                                    className="mt-0.5 size-4 accent-success"
                                />
                                <span className="text-[13px] font-medium">
                                    Anexo de designación de beneficiario
                                </span>
                                <span className="col-start-2 text-[11.5px] leading-relaxed text-muted-foreground">
                                    Firma electrónica del anexo con la persona designada y su
                                    porcentaje.{' '}
                                    <button
                                        type="button"
                                        className="underline decoration-dotted hover:text-foreground"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            // TD-005 del silo: aquí se abrirá el documento versionado cuando exista.
                                            toast.info('El documento aún no está disponible.');
                                        }}
                                    >
                                        Leer documento
                                    </button>
                                </span>
                            </label>
                        </Section>
                    </>
                )}
                {/* El fallo de la recarga se queda en pantalla: el vendedor tiene que poder leerlo
                    con el cliente enfrente, y de ahí decide si reintenta o sale sin el bono. */}
                {topup.isError && (
                    <p className="mx-auto mt-3.5 max-w-md rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-[11.5px] leading-relaxed text-destructive">
                        {welcomeTopupErrorMsg(topup.error)}
                    </p>
                )}
            </WizardCardBody>

            {/* La salida va al CENTRO, como en el mock (`10-bienvenida-nivel2.html`, §footPausar
                con `flex-1 justify-center`). Estaba pegada al «← Volver». */}
            <WizardFootNav
                back={<WizardBackLink sessionId={sessionId} step="delivery" />}
                exit={
                    <Button
                        variant="ghost"
                        onClick={() => {
                            setExitOpen(true);
                        }}
                    >
                        {copy.exitLabel}
                    </Button>
                }
            >
                <Button
                    disabled={!canActivate}
                    isLoading={topup.isPending}
                    rightIcon={<Check className="size-3.5" aria-hidden />}
                    onClick={() => void activate()}
                >
                    {copy.continueLabel}
                </Button>
            </WizardFootNav>

            <ConfirmDialog
                open={exitOpen}
                onOpenChange={setExitOpen}
                title={copy.exitTitle}
                description={copy.exitDescription}
                confirmLabel={copy.exitLabel}
                destructive
                loading={exitNoTopup.isPending}
                onConfirm={() => void exit()}
            />
        </WizardCard>
    );
}

/** Franja del regalo con su monto vigente; el confeti entra solo con el bono acreditado. */
function WelcomeGiftHero({
    bonus,
    celebrate,
}: {
    bonus: ReturnType<typeof useWelcomeBonus>;
    celebrate: boolean;
}) {
    return (
        <div className="relative mb-[18px] flex items-center gap-3 overflow-hidden rounded-[10px] border border-success-soft-border bg-success-soft/50 px-3.5 py-3">
            {celebrate && <Confetti />}
            <div className="flex size-9 flex-none items-center justify-center rounded-lg bg-success text-success-foreground">
                <Gift className="size-5" aria-hidden />
            </div>
            {/* 🔴 Sin monto no hay regalo que anunciar. Los puntos suspensivos se leen como
                "ya casi carga" y se quedan ahí, con el copy de abajo prometiendo el depósito. */}
            {bonus.isError ? (
                <div className="min-w-0 flex-1">
                    <InlineQueryError
                        className="text-[12.5px] font-medium"
                        message={`No se pudo leer el monto del regalo. ${wizardErrorMsg(bonus.error, 'Revisa la conexión.')}`}
                        onRetry={() => void bonus.refetch()}
                        retrying={bonus.isFetching}
                    />
                    <div className="mt-1 text-[11.5px] leading-[1.35] text-muted-foreground">
                        No le anuncies un monto al cliente hasta que aparezca.
                    </div>
                </div>
            ) : (
                <div className="min-w-0 flex-1">
                    <div className="text-[20px] font-bold leading-[1.1] tracking-[-0.01em] tabular-nums text-success">
                        {bonus.data ? formatMXN(bonus.data.amountCents / 100) : '…'}
                    </div>
                    <div className="mt-0.5 text-[11.5px] leading-[1.35] text-muted-foreground">
                        Se depositan en la cuenta PagoConFiado al terminar.
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * Cómo va el cierre del paso que se brinca. El error se muestra: una venta que no cerró y nadie se
 * entera es peor que una pantalla de más.
 */
function StepCloseStatus({
    error,
    retrying,
    onRetry,
}: {
    error: string | null;
    retrying: boolean;
    onRetry: () => void;
}) {
    if (error) {
        return (
            <span className="flex flex-wrap items-center gap-2 text-[12px] text-destructive">
                {error}
                <Button variant="outline" size="sm" isLoading={retrying} onClick={onRetry}>
                    Reintentar
                </Button>
            </span>
        );
    }
    return <span className="text-[12.5px] text-muted-foreground">Cerrando la venta…</span>;
}

// .input.input-sm del mock: alto 28px, texto 12px, borde de token.
const inputCls =
    'h-8 rounded-md border border-border bg-background px-2.5 text-[12px] outline-none focus:border-ring';

/**
 * `.section` del mock: bloque con fondo muted suave, header con paso numerado (`.section-step`)
 * y chip de estado a la derecha. Al completarse el paso, el círculo se pinta de success.
 */
function Section({
    n,
    title,
    done,
    chip,
    children,
}: {
    n: number;
    title: string;
    done: boolean;
    chip: string;
    children: ReactNode;
}) {
    return (
        <section className="mb-3.5 rounded-[10px] border border-border bg-muted/35 px-4 py-3.5">
            <div className="mb-2.5 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <span
                        className={cn(
                            'inline-flex size-[22px] items-center justify-center rounded-full border text-[11px] font-bold',
                            done
                                ? 'border-success bg-success text-success-foreground'
                                : 'border-border bg-card',
                        )}
                    >
                        {done ? <Check className="size-3" aria-hidden /> : n}
                    </span>
                    <span className="text-[13px] font-semibold">{title}</span>
                </div>
                <span
                    className={cn(
                        'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium leading-[1.35]',
                        done
                            ? 'border-success-soft-border bg-success-soft text-success'
                            : 'border-border bg-muted text-muted-foreground',
                    )}
                >
                    {chip}
                </span>
            </div>
            <div className="mt-1">{children}</div>
        </section>
    );
}
